import { Injectable } from '@angular/core';
import { UserSettingsController } from '@prlw/core/user-settings/user-settings.controller';
import { Observable } from 'rxjs';
import { distinctUntilChanged, first, map, switchMap } from 'rxjs/operators';
import { Setting } from '../user-settings/setting.interface';

export type AllShipmentsCodes = { [key: string]: string[] | undefined };
const MAIN_TABLE_SHIPMENTS_SETTING = new Setting<AllShipmentsCodes>(
  'main-table-shipments',
);

@Injectable({
  providedIn: 'root',
})
export class ShipmentsState {
  private readonly _state$: Observable<AllShipmentsCodes>;

  constructor(private readonly userSettingsController: UserSettingsController) {
    this._state$ = this.userSettingsController.setting$(
      MAIN_TABLE_SHIPMENTS_SETTING,
      {},
    );
  }

  public getSelectedShipments(productKey: string): Observable<string[]> {
    return this._state$.pipe(
      map((state) => state[productKey] ?? []),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    );
  }

  public setSelectedShipments(
    productKey: string,
    shipmentCodes: string[],
  ): void {
    if (!shipmentCodes.length) {
      return;
    }
    this._state$
      .pipe(
        first(),
        switchMap((prevState) =>
          this.userSettingsController.updateSetting(
            MAIN_TABLE_SHIPMENTS_SETTING,
            {
              ...prevState,
              [productKey]: shipmentCodes,
            },
          ),
        ),
      )
      .subscribe();
  }

  public addShipmentToSelected(productKey: string, shipmentCode: string): void {
    this._state$
      .pipe(
        first(),
        switchMap((prevState) => {
          const prevShipments = prevState[productKey] ?? [];
          return this.userSettingsController.updateSetting(
            MAIN_TABLE_SHIPMENTS_SETTING,
            {
              ...prevState,
              [productKey]: [...prevShipments, shipmentCode],
            },
          );
        }),
      )
      .subscribe();
  }

  public removeShipmentFromSelected(
    productKey: string,
    shipmentCode: string,
  ): void {
    this._state$
      .pipe(
        first(),
        switchMap((prevState) => {
          const prevShipments = prevState[productKey] ?? [];
          return this.userSettingsController.updateSetting(
            MAIN_TABLE_SHIPMENTS_SETTING,
            {
              ...prevState,
              [productKey]: prevShipments.filter(
                (item) => item !== shipmentCode,
              ),
            },
          );
        }),
      )
      .subscribe();
  }
}
